import { Link } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "design-system/molecules/Card"
import { cn } from "design-system/utils"
import { Ruler } from "lucide-react"
import { HeaderButtons } from "modules/shared/components/Breadcrumbs/HeaderButtons"
import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { ConvertSizeChartResponseData } from "schema"
import { z } from "zod"
import { CloneExistingSizeChartDialog } from "./CloneExistingSizeChartDialog"
import { ConvertSizeChartConvertForm } from "./ConvertSizeChartConvertForm"
import type { GeneratedSizeChartImage } from "./ConvertSizeChartGenerateImageForm"
import { ConvertSizeChartGenerateImageForm } from "./ConvertSizeChartGenerateImageForm"
import { ConvertSizeChartUpdateForm } from "./ConvertSizeChartUpdateForm"

type ConvertSizeChartResult = z.infer<typeof ConvertSizeChartResponseData>

const CONVERT_SIZE_CHART_ROUTE = "/settings/integrations/shopify/SizeChart"

function areImageDimensionsEqual(
  currentImage: GeneratedSizeChartImage | null,
  nextImage: GeneratedSizeChartImage | null,
) {
  const currentDimensions = currentImage?.dimensions ?? []
  const nextDimensions = nextImage?.dimensions ?? []

  if (currentDimensions.length !== nextDimensions.length) return false

  return currentDimensions.every((currentDimension, index) => {
    const nextDimension = nextDimensions[index]
    if (!nextDimension) return false

    return (
      currentDimension.id === nextDimension.id &&
      currentDimension.text === nextDimension.text &&
      currentDimension.start.x === nextDimension.start.x &&
      currentDimension.start.y === nextDimension.start.y &&
      currentDimension.end.x === nextDimension.end.x &&
      currentDimension.end.y === nextDimension.end.y &&
      currentDimension.guide.x === nextDimension.guide.x &&
      currentDimension.guide.y === nextDimension.guide.y
    )
  })
}

function areImageTransformsEqual(
  currentImage: GeneratedSizeChartImage | null,
  nextImage: GeneratedSizeChartImage | null,
) {
  const currentTransform = currentImage?.imageTransform
  const nextTransform = nextImage?.imageTransform

  if (!currentTransform && !nextTransform) return true
  if (!currentTransform || !nextTransform) return false

  return (
    currentTransform.offset.x === nextTransform.offset.x &&
    currentTransform.offset.y === nextTransform.offset.y &&
    currentTransform.scale === nextTransform.scale
  )
}

export const ConvertSizeChartCard = () => {
  const { t } = useTranslation()

  return (
    <Link to={CONVERT_SIZE_CHART_ROUTE} className="block w-full">
      <Card className="hover:bg-muted duration-150 cursor-pointer break-all">
        <CardHeader className="flex flex-row gap-4">
          <div>
            <Ruler size={32} />
          </div>
          <div className="flex flex-col gap-2 items-start text-left grow break-words">
            <CardTitle>{t("settings.integrations.shopify.convertSizeChart.title")}</CardTitle>
            <CardDescription>
              {t("settings.integrations.shopify.convertSizeChart.description")}
            </CardDescription>
          </div>
        </CardHeader>
      </Card>
    </Link>
  )
}

export const ConvertSizeChart = () => {
  const { t } = useTranslation()
  const [activeStep, setActiveStep] = useState(0)
  const [isCloneDialogOpen, setIsCloneDialogOpen] = useState(false)
  const [generatedImage, setGeneratedImage] = useState<GeneratedSizeChartImage | null>(null)
  const [result, setResult] = useState<ConvertSizeChartResult | null>(null)
  const steps = [
    t("settings.integrations.shopify.convertSizeChart.steps.importSizeChart"),
    t("settings.integrations.shopify.convertSizeChart.steps.importProductImage"),
    t("settings.integrations.shopify.convertSizeChart.steps.selectProduct"),
  ]
  const canSelectStep = (step: number) => {
    if (step === 0) return true
    if (step === 1) return Boolean(result)

    return Boolean(result && generatedImage?.base64Image)
  }

  const handleStepChange = (step: number) => {
    if (!canSelectStep(step)) return
    setActiveStep(step)
  }

  const handleConverted = (
    convertedResult: ConvertSizeChartResult | null,
    options?: { resetGeneratedImage?: boolean },
  ) => {
    setResult(convertedResult)

    if (options?.resetGeneratedImage) {
      setGeneratedImage(null)
    }

    if (!convertedResult) {
      setActiveStep(0)
    }
  }

  const handleGeneratedImageChange = useCallback((image: GeneratedSizeChartImage | null) => {
    setGeneratedImage((currentImage) => {
      if (
        currentImage?.url === image?.url &&
        currentImage?.sourceImage === image?.sourceImage &&
        currentImage?.base64Image === image?.base64Image &&
        areImageTransformsEqual(currentImage, image) &&
        areImageDimensionsEqual(currentImage, image)
      ) {
        return currentImage
      }

      return image
    })
  }, [])

  return (
    <>
      <HeaderButtons>
        <Button type="button" variant="default" onClick={() => setIsCloneDialogOpen(true)}>
          {t("settings.integrations.shopify.convertSizeChart.cloneExistingProduct")}
        </Button>
      </HeaderButtons>
      <CloneExistingSizeChartDialog
        isOpen={isCloneDialogOpen}
        onOpenChange={setIsCloneDialogOpen}
        onCloned={({ generatedImage: clonedImage, result: clonedResult }) => {
          setResult(clonedResult)
          setGeneratedImage(clonedImage)
          setActiveStep(0)
        }}
      />
      <div className="flex min-w-0 flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-semibold">
            {t("settings.integrations.shopify.convertSizeChart.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("settings.integrations.shopify.convertSizeChart.description")}
          </p>
        </div>
        <ol className="grid gap-2 md:grid-cols-3">
          {steps.map((step, index) => {
            const isActive = activeStep === index
            const isCompleted = result && index < activeStep
            const isSelectable = canSelectStep(index)

            return (
              <li key={step}>
                <button
                  type="button"
                  disabled={!isSelectable}
                  onClick={() => handleStepChange(index)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-md border px-4 py-3 text-left",
                    "transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                    isActive && "border-primary bg-primary/5",
                    !isActive && "hover:bg-muted",
                  )}
                >
                  <span
                    className={cn(
                      "flex size-7 shrink-0 items-center justify-center rounded-full border " +
                        "text-sm",
                      isActive && "border-primary bg-primary text-primary-foreground",
                      isCompleted && "border-success bg-success/10 text-success",
                    )}
                  >
                    {index + 1}
                  </span>
                  <span className="text-sm font-medium">{step}</span>
                </button>
              </li>
            )
          })}
        </ol>
        <Card className="min-w-0">
          <CardHeader>
            <CardTitle>{steps[activeStep]}</CardTitle>
          </CardHeader>
          <CardContent className="flex min-w-0 flex-col gap-4">
            {activeStep === 0 && (
              <>
                <ConvertSizeChartConvertForm isOpen result={result} onConverted={handleConverted} />
                {result && (
                  <div className="flex justify-end">
                    <Button type="button" onClick={() => setActiveStep(1)}>
                      {t("shared.next")}
                    </Button>
                  </div>
                )}
              </>
            )}
            {activeStep === 1 && result && (
              <>
                <ConvertSizeChartGenerateImageForm
                  generatedImage={generatedImage}
                  isOpen
                  onGeneratedImageChange={handleGeneratedImageChange}
                  result={result}
                />
                <div className="flex justify-between gap-2">
                  <Button type="button" variant="outline" onClick={() => setActiveStep(0)}>
                    {t("shared.back")}
                  </Button>
                  <Button
                    type="button"
                    disabled={!generatedImage?.base64Image}
                    onClick={() => setActiveStep(2)}
                  >
                    {t("shared.next")}
                  </Button>
                </div>
              </>
            )}
            {activeStep === 2 && result && (
              <ConvertSizeChartUpdateForm generatedImage={generatedImage} isOpen result={result} />
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}
